import { useCallback, useState } from 'react';
import { z } from 'zod';
import { getLocalStorage } from '@trinity/chat-components/lib/storage';

const RECENT_KEY_PREFIX = 'trinity:add-widget:recent:v1';
const MAX_RECENT = 5;

const RecentIdsSchema = z.array(z.string());

const storageKey = (scope: string): string => `${RECENT_KEY_PREFIX}:${scope}`;

/** The stored ids for a scope, or none when nothing valid is stored or storage is unavailable. */
function readRecentIds(scope: string): string[] {
  try {
    const raw = getLocalStorage().getItem(storageKey(scope));
    if (raw === null) return [];
    const parsed = RecentIdsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

/** `id` first, any earlier copy of it dropped, capped at the most recent few. */
export const withRecent = (ids: readonly string[], id: string): string[] =>
  [id, ...ids.filter((existing) => existing !== id)].slice(0, MAX_RECENT);

/**
 * The widgets last added from the Add Widget page, newest first, kept in this browser's local
 * storage under `scope` (one list per kind of builder). Only picker item ids are stored, never
 * widget content; the page drops ids it can no longer offer. Without a `scope` nothing is kept.
 */
export function useRecentItems(scope: string | undefined): {
  recentIds: readonly string[];
  recordRecent: (id: string) => void;
} {
  // Read once when the page opens; adding a widget closes it, so the list never changes while shown.
  const [recentIds] = useState<string[]>(() => (scope ? readRecentIds(scope) : []));

  const recordRecent = useCallback(
    (id: string) => {
      if (!scope) return;
      try {
        getLocalStorage().setItem(storageKey(scope), JSON.stringify(withRecent(readRecentIds(scope), id)));
      } catch {
        // Storage can be full or blocked; the list is a convenience, so adding the widget carries on.
      }
    },
    [scope],
  );

  return { recentIds, recordRecent };
}
